import { createBall, copyBall, launchBall, puttBall, simulate, stepBall, SIM_DT, BALL_R, CUP_R } from './ballSim.js';
import { speedAt } from './clubs.js';

/**
 * Turning a decision (club, meter reading, aim, strike quality, lie) into a
 * launch — and the planning helpers built on the simulator: the aim preview,
 * the putt pace solver, and the putt meter scale. Shared by the game and the
 * headless bot so both hit exactly the same shot. Pure module.
 */

export const PERFECT_STRIKE = {
  grade: 'pure', early: false, contact: 'clean',
  yawDeg: 0, loftDelta: 0, speedFactor: 1, spinFactor: 1, sidespinAdd: 0, timing: 0,
};

/**
 * @param {object} o
 * @param {object} o.club
 * @param {number} o.power  0..100 meter reading
 * @param {number} o.dirX,o.dirZ  horizontal aim (unit)
 * @param {object} o.lie    from clubs.lieFor()
 * @param {object} [o.strike] from swing.strikeFromTiming()
 * @param {number} [o.scatter] -1..1 seeded roll for lie scatter
 * @param {number} [o.slope] ground rise per yard along the aim line
 */
export function buildLaunch({ club, power, dirX, dirZ, lie, strike = PERFECT_STRIKE, scatter = 0, slope = 0 }) {
  // + yaw = pull (left of aim). Right of aim is (-dirZ, dirX), so left is a
  // negative rotation in this frame.
  const yaw = (-(strike.yawDeg || 0) + scatter * lie.scatterDeg) * Math.PI / 180;
  const c = Math.cos(yaw), s = Math.sin(yaw);
  return {
    speed: speedAt(club, power) * lie.speedFactor * strike.speedFactor,
    dirX: dirX * c - dirZ * s,
    dirZ: dirX * s + dirZ * c,
    // Always launch clear of the slope in front of the ball
    loftDeg: Math.max(4, club.loft + lie.loftBonus + strike.loftDelta, Math.atan(slope) * 180 / Math.PI + 9),
    back: club.spin * lie.spinFactor * strike.spinFactor,
    // Early = closed face = draws left
    side: -(strike.sidespinAdd || 0),
  };
}

/**
 * Flight preview for the aim line: the arc to the first landing, then the
 * bounce and roll to where the ball stops (`roll` points, `restX/restZ`).
 * Ignores wind.
 */
export function previewShot(world, ball, launch) {
  const b = copyBall(ball);
  launchBall(b, launch);
  const run = simulate(b, world, null, { sample: 0.08, stopOnLand: true, maxTime: 12 });
  const landX = b.x, landY = b.y, landZ = b.z, landed = b.landed;
  const out = simulate(b, world, null, { sample: 0.1, maxTime: 10 });
  return {
    points: run.points, landX, landY, landZ, landed,
    roll: out.points, restX: b.x, restZ: b.z, water: b.mode === 'water' || b.mode === 'lost',
    blocked: run.events.some((e) => e.type === 'tree'),
  };
}

/**
 * The power (0-100) at which a pure strike in still air comes to REST
 * `want` away: book distances are for flat turf, and slopes, bounces, the
 * roll on this grass and a green up or down the hill all move where a ball
 * really stops. A few test shots, secant-stepped from the book's guess.
 * Returns null if even full power stops short. Pure; ~4 simulations.
 */
export function powerForRest(world, ball, { club, lie, dirX, dirZ, slope, want, book }) {
  const restAt = (power) => {
    const b = copyBall(ball);
    launchBall(b, buildLaunch({ club, power, dirX, dirZ, lie, slope }));
    simulate(b, world, null, { maxTime: 22 });
    return Math.hypot(b.x - ball.x, b.z - ball.z);
  };
  const full = restAt(100);
  if (full < want) return null;
  let p0 = Math.max(5, Math.min(100, book ?? 60)), d0 = restAt(p0);
  let p1 = Math.max(4, Math.min(100, p0 * Math.sqrt(want / Math.max(1, d0)))), d1 = restAt(p1);
  for (let i = 0; i < 4 && Math.abs(d1 - want) > 0.6; i++) {
    const slopeD = (d1 - d0) / (p1 - p0 || 1e-6);
    const next = Math.abs(slopeD) > 1e-4 ? p1 + (want - d1) / slopeD : p1 * Math.sqrt(want / Math.max(1, d1));
    p0 = p1; d0 = d1;
    p1 = Math.max(3, Math.min(100, next));
    d1 = restAt(p1);
  }
  return p1;
}

/**
 * The power at which a pure strike in still air first comes DOWN at
 * (tx, tz)'s distance (its carry, not where it stops): for landing on a
 * mushroom. Null if full power cannot reach it.
 */
export function powerForCarry(world, ball, { club, lie, dirX, dirZ, slope, want, book, env = null, atY = null }) {
  // (atY: where it comes down through that height, e.g. a mushroom's cap)
  const carryAt = (power) => {
    const b = copyBall(ball);
    launchBall(b, buildLaunch({ club, power, dirX, dirZ, lie, slope }));
    for (let t = 0; t < 14 && b.mode === 'air' && !b.landed; t += SIM_DT) {
      stepBall(b, world, env, SIM_DT, []);
      if (atY !== null && b.vy < 0 && b.y <= atY) break;
    }
    return Math.hypot(b.x - ball.x, b.z - ball.z);
  };
  if (carryAt(100) < want) return null;
  let p0 = Math.max(5, Math.min(100, book ?? 60)), d0 = carryAt(p0);
  let p1 = Math.max(4, Math.min(100, p0 * Math.sqrt(want / Math.max(1, d0)))), d1 = carryAt(p1);
  for (let i = 0; i < 5 && Math.abs(d1 - want) > 0.4; i++) {
    const k = (d1 - d0) / (p1 - p0 || 1e-6);
    const next = Math.abs(k) > 1e-4 ? p1 + (want - d1) / k : p1 * Math.sqrt(want / Math.max(1, d1));
    p0 = p1; d0 = d1; p1 = Math.max(3, Math.min(100, next)); d1 = carryAt(p1);
  }
  return p1;
}

/**
 * Where a pure strike at `power` along (dirX, dirZ) comes down through
 * height atY (or lands), with the wind: for steering a locked shot.
 */
export function carryPoint(world, ball, { club, lie, dirX, dirZ, slope, power, env = null, atY = null }) {
  const b = copyBall(ball);
  launchBall(b, buildLaunch({ club, power, dirX, dirZ, lie, slope }));
  for (let t = 0; t < 14 && b.mode === 'air' && !b.landed; t += SIM_DT) {
    stepBall(b, world, env, SIM_DT, []);
    if (atY !== null && b.vy < 0 && b.y <= atY) break;
  }
  return { x: b.x, z: b.z };
}

/** Full roll-out of a putt along a line (cup included). */
export function previewPutt(world, ball, dirX, dirZ, speed) {
  const b = copyBall(ball);
  puttBall(b, { speed, dirX, dirZ });
  const run = simulate(b, world, null, { sample: 0.06, maxTime: 20 });
  return { points: run.points, holed: b.mode === 'holed', endX: b.x, endZ: b.z };
}

/**
 * Speed that rolls the ball `distance` (plus a little) along its line,
 * measured with the cup removed so "dead weight" is well defined.
 */
export function puttSpeedFor(world, ball, dirX, dirZ, distance, past = 0.45) {
  const noCup = { ...world, cup: null };
  const want = distance + past;
  // Start from a ceiling near the answer, not 30 m/s: a putt struck at 30
  // rolls for the full 20 simulated seconds, and half the old search was
  // spent rolling balls off the green. Raise it until it is enough.
  const reach = (speed) => {
    const b = copyBall(ball);
    puttBall(b, { speed, dirX, dirZ });
    simulate(b, noCup, null, { maxTime: 20 });
    return { rolled: (b.x - ball.x) * dirX + (b.z - ball.z) * dirZ, rest: b.mode === 'rest' };
  };
  let lo = 0.2, hi = Math.min(30, 1.5 + want * 0.9);
  for (let k = 0; k < 5; k++) { const r = reach(hi); if (r.rolled >= want || !r.rest) break; lo = hi; hi = Math.min(30, hi * 1.8); }
  for (let i = 0; i < 12; i++) {
    const mid = (lo + hi) / 2;
    const b = copyBall(ball);
    puttBall(b, { speed: mid, dirX, dirZ });
    simulate(b, noCup, null, { maxTime: 20 });
    const rolled = (b.x - ball.x) * dirX + (b.z - ball.z) * dirZ;
    if (rolled < want && b.mode === 'rest') lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * Putt meter top speed: the meter is rescaled per putt so the ideal pace
 * always sits in the readable part of the bar (never squeezed at the bottom).
 */
export function puttMeterMax(idealSpeed) {
  return Math.max(3, idealSpeed / 0.68);
}

/** Meter reading (0..100) -> putt speed. */
export function puttSpeedAt(maxSpeed, power) {
  return maxSpeed * Math.max(0.03, power / 100);
}

/**
 * The caddie's read: search aim angles around the hole for the line that
 * drops (or finishes nearest). Returns { dirX, dirZ, speed, holed }.
 */
export function readPutt(world, ball, span = 0.35, steps = 29) {
  const cx = world.cup.x - ball.x, cz = world.cup.z - ball.z;
  const dist = Math.hypot(cx, cz);
  const base = Math.atan2(cz, cx);
  let best = null;
  for (let i = 0; i < steps; i++) {
    const a = base + (i / (steps - 1) - 0.5) * 2 * span;
    const dirX = Math.cos(a), dirZ = Math.sin(a);
    const speed = puttSpeedFor(world, ball, dirX, dirZ, dist);
    const b = copyBall(ball);
    puttBall(b, { speed, dirX, dirZ });
    simulate(b, world, null, { maxTime: 20 });
    const miss = b.mode === 'holed' ? -1 / (1 + Math.abs(a - base)) : Math.hypot(b.x - world.cup.x, b.z - world.cup.z);
    if (!best || miss < best.miss) best = { dirX, dirZ, speed, miss, holed: b.mode === 'holed' };
  }
  return best;
}

/** Ground rise per yard looking along (dirX, dirZ) from the ball. */
export function slopeAlong(world, ball, dirX, dirZ) {
  const g = [0, 0];
  world.gradAt(ball.x, ball.z, g);
  const ahead = (world.heightAt(ball.x + dirX * 1.5, ball.z + dirZ * 1.5) - world.heightAt(ball.x, ball.z)) / 1.5;
  return Math.max(g[0] * dirX + g[1] * dirZ, ahead);
}

export { BALL_R, CUP_R, createBall };
